// A URL that came from a user, a message or a third party must not be put into an href
// or handed to window.open() unless it is http(s): React 18 does not block `javascript:`
// hrefs, and window.open('javascript:...') runs in the opener's origin.

// What the browser's URL parser ignores, so the check sees what the browser would:
// tab/LF/CR are removed anywhere, and leading/trailing C0 controls and spaces are trimmed.
// (`jav<TAB>ascript:alert(1)` is a javascript: URL.)
export function normalizeLikeBrowser(url) {
  const withoutLineBreaks = [...url]
    .filter((c) => {
      const code = c.charCodeAt(0);
      return code !== 9 && code !== 10 && code !== 13;
    })
    .join('');
  let start = 0;
  let end = withoutLineBreaks.length;
  while (start < end && withoutLineBreaks.charCodeAt(start) <= 32) start += 1;
  while (end > start && withoutLineBreaks.charCodeAt(end - 1) <= 32) end -= 1;
  return withoutLineBreaks.slice(start, end);
}

// Any origin works: it is only compared with itself.
const SITE_ORIGIN = 'https://site.invalid';

/**
 * A link target for a router link (`to=`): a rooted path that stays on this site
 * ("/profile/1"); otherwise null. Nothing else: every link this app builds for a router is a
 * path inside the app, so an absolute URL is not needed and is not allowed (least privilege).
 * A router renders an absolute or protocol-relative `to` as a plain <a href>, so a
 * `javascript:` value, "//host", or a path with a backslash, or a tab, right after the slash
 * (which a browser reads as "//host") must not reach it. The path is judged by the URL parser
 * on the browser-cleaned value, and that cleaned value is what is returned, so the value that
 * was checked is the value used. Callers render null as no link.
 */
export function toSafeLinkTarget(link) {
  if (typeof link !== 'string') return null;
  const cleaned = normalizeLikeBrowser(link);
  if (!cleaned.startsWith('/')) return null;
  try {
    return new URL(cleaned, SITE_ORIGIN).origin === SITE_ORIGIN ? cleaned : null;
  } catch {
    return null;
  }
}

/**
 * The URL, cleaned the way a browser would clean it, when it is an absolute http(s) URL;
 * otherwise null. Callers render a null as plain text, never as a link.
 */
export function toSafeHttpUrl(url) {
  if (typeof url !== 'string') return null;
  const cleaned = normalizeLikeBrowser(url);
  try {
    const { protocol } = new URL(cleaned);
    return protocol === 'http:' || protocol === 'https:' ? cleaned : null;
  } catch {
    return null;
  }
}

